import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

export default function IndianSchoolOnboarding() {
  const faqData = [
    {
      question: 'How is student data protected?',
      answer:
        'We only require a username for students, never personal details like emails or phone numbers. All data is securely stored and encrypted.',
    },
    {
      question: 'What if a student forgets their password?',
      answer:
        "Any designated Admin or Student Manager from your school can instantly reset a student's password from their dashboard.",
    },
    {
      question: 'Can we add or remove students at any time?',
      answer:
        'Yes. School Admins can add new students or deactivate accounts for students who have left the school whenever needed.',
    },
    {
      question: 'Can we upload all our students at once?',
      answer:
        'Yes, with Option A (Use Your Student IDs), you can upload a simple list of numbers to create all accounts simultaneously.',
    },
  ];

  return (
    <>
      <title>Onboarding for Indian Schools</title>
      <meta
        name="description"
        content="Simple & Secure School-Wide Onboarding"
      />
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <header>
          <Navbar />
        </header>

        {/* Main Content */}
        <Box component="main" flexGrow={1}>
          {/* Hero Section */}
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              background: 'white',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Container
              sx={{
                maxWidth: { xs: '100%', sm: '95%', md: '1460px' },
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h4"
                component="h3"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  mb: 3,
                  mt: 6,
                }}
              >
                Simple & Secure School-Wide Onboarding
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: 700,
                  mx: 'auto',
                  mb: 4,
                  opacity: 0.95,
                  lineHeight: 1.6,
                }}
              >
                Get all your students ready to play in minutes. No student email
                or phone numbers required.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                className="quiz-btn btn btn-primary"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  borderRadius: '50px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Start Onboarding Process
              </Button>
            </Container>
          </Box>

          {/* 3-Step Process Section */}
          <Box sx={{ py: 10 }} className="features-item">
            <Container
              sx={{
                maxWidth: { xs: '100%', sm: '95%', md: '1460px' },
              }}
            >
              <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  mb: 4,
                  mt: 2,
                }}
                className="item-heading"
              >
                Onboard Your Entire School in Three Easy Steps
              </Typography>

              <Grid container spacing={4} justifyContent="center">
                {/* Step 1 */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={6}
                    sx={{
                      padding: 4,
                      textAlign: 'center',
                      height: '100%',
                      borderRadius: '20px',
                      border: '1px solid #e3f2fd',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                    className="item-inner-box"
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: '#2A9235',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 'bold', mb: 2 }}
                    >
                      Step 1: Register School
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                    >
                      Create your school's master account in under two minutes.
                    </Typography>
                  </Paper>
                </Grid>

                {/* Step 2 */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={6}
                    sx={{
                      padding: 4,
                      textAlign: 'center',
                      height: '100%',
                      borderRadius: '20px',
                      border: '1px solid #e3f2fd',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                    className="item-inner-box"
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: '#2A9235',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 'bold', mb: 2 }}
                    >
                      Step 2: Add Personnel
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                    >
                      Invite admins and teachers who can manage student
                      accounts.
                    </Typography>
                  </Paper>
                </Grid>

                {/* Step 3 */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={6}
                    sx={{
                      padding: 4,
                      textAlign: 'center',
                      height: '100%',
                      borderRadius: '20px',
                      background:
                        'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                      border: '1px solid #e3f2fd',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                    className="item-inner-box"
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: '#2A9235',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 'bold', mb: 2 }}
                    >
                      Step 3: Create Student Logins
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                    >
                      Instantly generate secure accounts for all your students.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Detailed Steps Section */}
          <Box sx={{ py: 10, backgroundColor: '#f8f9ff' }}>
            <Container
              sx={{
                maxWidth: { xs: '100%', sm: '95%', md: '1460px' },
              }}
            >
              <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  mb: 8,
                }}
                className="item-heading"
              >
                How It Works in Detail
              </Typography>

              {/* Step 1 Detail */}
              <Box sx={{ mb: 8 }}>
                <Paper
                  elevation={6}
                  sx={{
                    p: 0,
                    borderRadius: '24px',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                    border: '1px solid #e3f2fd',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '6px',
                      background: 'linear-gradient(90deg, #2A9235 0%, #34a843 100%)',
                    }
                  }}
                >
                  <Box sx={{ p: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #2A9235 0%, #34a843 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3,
                          boxShadow: '0 8px 20px rgba(42, 146, 53, 0.3)',
                        }}
                      >
                        <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 'bold', color: '#2A9235', mb: 1 }}
                        >
                          Step 1: Create Your School's Master Account
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: '#2A9235',
                            backgroundColor: 'rgba(42, 146, 53, 0.1)',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            display: 'inline-block',
                            fontWeight: 'medium'
                          }}
                        >
                          School Registration
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                    >
                      Provide your school's official details and create the primary admin account. 
                      This admin will have full control over your school's dashboard.
                    </Typography>
                    
                    <Paper
                      elevation={2}
                      sx={{
                        p: 4,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(42, 146, 53, 0.08) 100%)',
                        borderRadius: '16px',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        textAlign: 'center',
                        minHeight: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          opacity: 0.8,
                        }}
                      >
                        Visual Aid: School Registration Form Preview
                      </Typography>
                    </Paper>
                  </Box>
                </Paper>
              </Box>

              {/* Step 2 Detail */}
              <Box sx={{ mb: 8 }}>
                <Paper
                  elevation={6}
                  sx={{
                    p: 0,
                    borderRadius: '24px',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                    border: '1px solid #e3f2fd',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '6px',
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    }
                  }}
                >
                  <Box sx={{ p: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3,
                          boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                        }}
                      >
                        <GroupIcon sx={{ fontSize: 32, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 'bold', color: '#667eea', mb: 1 }}
                        >
                          Step 2: Onboard Your Personnel
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: '#667eea',
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            display: 'inline-block',
                            fontWeight: 'medium'
                          }}
                        >
                          Assign Roles
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                    >
                      Your admin can invite other personnel from their dashboard. You can assign them as 
                      <strong> Admins</strong> (who can also add staff) or <strong>Student Managers</strong> (who can only manage students).
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(255, 255, 255, 0.9) 100%)',
                            border: '1px solid rgba(102, 126, 234, 0.2)',
                            height: '100%',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#667eea' }}>
                            Admins
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                            Full access to invite staff, manage students, and oversee school operations
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(255, 255, 255, 0.9) 100%)',
                            border: '1px solid rgba(102, 126, 234, 0.2)',
                            height: '100%',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#667eea' }}>
                            Student Managers
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                            Focus on student account management, password resets, and academic oversight
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 4,
                        mt: 4,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(42, 146, 53, 0.08) 100%)',
                        borderRadius: '16px',
                        border: '1px solid rgba(102, 126, 234, 0.2)',
                        textAlign: 'center',
                        minHeight: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          opacity: 0.8,
                        }}
                      >
                        Visual Aid: Students & Admins Dashboard Manager Preview
                      </Typography>
                    </Paper>
                  </Box>
                </Paper>
              </Box>

              {/* Step 3 Detail */}
              <Box>
                <Paper
                  elevation={6}
                  sx={{
                    p: 0,
                    borderRadius: '24px',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                    border: '1px solid #e3f2fd',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '6px',
                      background: 'linear-gradient(90deg, #ff6b6b 0%, #ffa726 100%)',
                    }
                  }}
                >
                  <Box sx={{ p: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3,
                          boxShadow: '0 8px 20px rgba(255, 107, 107, 0.3)',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 32, color: 'white' }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 'bold', color: '#ff6b6b', mb: 1 }}
                        >
                          Step 3: Add Your Students Instantly
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ 
                            color: '#ff6b6b',
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            display: 'inline-block',
                            fontWeight: 'medium'
                          }}
                        >
                          Choose Your Method
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography
                      variant="h6"
                      sx={{ color: 'text.secondary', mb: 6, lineHeight: 1.7 }}
                    >
                      We offer two private and secure methods for creating student accounts. 
                      Choose the one that works best for your school.
                    </Typography>

                    <Grid container spacing={4}>
                      {/* Option A */}
                      <Grid item xs={12} lg={6}>
                        <Paper
                          elevation={4}
                          sx={{
                            p: 4,
                            borderRadius: '20px',
                            background: 'linear-gradient(145deg, #ffffff 0%, rgba(42, 146, 53, 0.02) 100%)',
                            border: '2px solid #2A9235',
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '"A"',
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: '#2A9235',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '1.2rem',
                            }
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 'bold', mb: 2, color: '#2A9235' }}
                          >
                            Use Your Student IDs
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ mb: 3, lineHeight: 1.6, color: 'text.secondary' }}
                          >
                            You provide a list of existing unique student numbers 
                            (e.g., admission or roll numbers). We use this number for 
                            both the username and initial password.
                          </Typography>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: 'rgba(42, 146, 53, 0.1)',
                              borderRadius: '12px',
                              mb: 3,
                              border: '1px solid rgba(42, 146, 53, 0.2)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'bold', color: '#2A9235' }}
                            >
                              Best For: Schools with existing student ID systems
                            </Typography>
                          </Box>
                          <Paper
                            elevation={2}
                            sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, rgba(42, 146, 53, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)',
                              borderRadius: '12px',
                              border: '1px solid rgba(42, 146, 53, 0.2)',
                              textAlign: 'center',
                              minHeight: '80px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                            >
                              Visual: Student ID Upload Preview
                            </Typography>
                          </Paper>
                        </Paper>
                      </Grid>

                      {/* Option B */}
                      <Grid item xs={12} lg={6}>
                        <Paper
                          elevation={4}
                          sx={{
                            p: 4,
                            borderRadius: '20px',
                            background: 'linear-gradient(145deg, #ffffff 0%, rgba(102, 126, 234, 0.02) 100%)',
                            border: '2px solid #667eea',
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '"B"',
                              position: 'absolute',
                              top: 16,
                              right: 16,
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: '#667eea',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '1.2rem',
                            }
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 'bold', mb: 2, color: '#667eea' }}
                          >
                            Let Us Create Usernames
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ mb: 3, lineHeight: 1.6, color: 'text.secondary' }}
                          >
                            You tell us how many students you need. We generate 
                            secure, random usernames and passwords for them.
                          </Typography>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: 'rgba(102, 126, 234, 0.1)',
                              borderRadius: '12px',
                              mb: 3,
                              border: '1px solid rgba(102, 126, 234, 0.2)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'bold', color: '#667eea' }}
                            >
                              Best For: Maximum privacy & security
                            </Typography>
                          </Box>
                          <Paper
                            elevation={2}
                            sx={{
                              p: 3,
                              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)',
                              borderRadius: '12px',
                              border: '1px solid rgba(102, 126, 234, 0.2)',
                              textAlign: 'center',
                              minHeight: '80px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                            >
                              Visual: Account Generation Interface Preview
                            </Typography>
                          </Paper>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Box>
            </Container>
          </Box>

          {/* FAQ Section */}
          <Box >
            <Container
              sx={{
                maxWidth: { xs: '100%', sm: '95%', md: '1460px' },
              }}
            >
              <Typography
                variant="h3"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  mb: 8,
                }}
                className="item-heading"
              >
                Your Questions, Answered
              </Typography>

              <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                {faqData.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      mb: 3,
                      borderRadius: '15px !important',
                      border: '1px solid #e3f2fd',
                      '&:before': { display: 'none' },
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        py: 2,
                        px: 3,
                        '&.Mui-expanded': {
                          background: 'rgba(102, 126, 234, 0.05)',
                        },
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3 }}>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                      >
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Container>
          </Box>

          {/* Call to Action Section */}
          <Box
            sx={{
              py: 15, // Increased vertical padding
              textAlign: 'center',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 2,
            }}
            className="features-item"
          >
            <Container
              maxWidth="lg"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4, // Consistent gap between elements
              }}
            >
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                }}
              >
                Ready to Make Your School a Spelling Hero?
              </Typography>

              <Button
                variant="contained"
                color="secondary"
                className="quiz-btn btn btn-primary"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  borderRadius: '50px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Register Your School Now
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  maxWidth: '600px',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    cursor: 'pointer',
                    opacity: 0.9,
                    '&:hover': {
                      opacity: 1,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                  }}
                >
                  Need to share this with your team? Download our School
                  Onboarding Guide (PDF).
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    cursor: 'pointer',
                    opacity: 0.9,
                    '&:hover': {
                      opacity: 1,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                  }}
                >
                  Have more questions? Contact Us.
                </Typography>
              </Box>
            </Container>
          </Box>
        </Box>

        <footer>
          <Footer />
        </footer>
      </Box>
    </>
  );
}
